import { BaseService } from "../baseService";
import { FileAPIService } from "../fileAPIService";
import { VendorAPIService } from "./vendorAPIService";
import { sanitiseParams } from "src/utils";
import { ValidationError } from "src/errors";
import {
  GetVendorsQueryParams,
  PaginatedVendors,
  PatchVendorRequestBody,
  PostVendorRequestBody,
  Vendor
} from "src/types";
import { PostVendorsExportsRequestBody } from "src/types/api/vendor/postVendorsExports";
import { PostVendorsBulkEmailRequestBody, SendBulkEmailResult } from "src/types/api/vendor/postVendorsBulkEmail";

class VendorService extends BaseService<VendorAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: VendorAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }
  @sanitiseParams()
  async getVendors(queryParams: GetVendorsQueryParams): Promise<PaginatedVendors> {
    return await this.apiService.getVendors(queryParams);
  }

  @sanitiseParams()
  async getVendorNames(bypassCache: boolean = false): Promise<string[]> {
    const vendorNames = await this.apiService.getVendorNames(bypassCache);

    return vendorNames;
  }

  @sanitiseParams()
  async getVendorLocations(): Promise<string[]> {
    const vendorLocations = await this.apiService.getVendorLocations();

    return vendorLocations;
  }

  @sanitiseParams()
  async getVendorByName(name: string): Promise<Vendor> {
    const vendor = await this.apiService.getVendorByName(name);

    return vendor;
  }

  @sanitiseParams()
  async getVendorById(id: string): Promise<Vendor> {
    const vendor = await this.apiService.getVendorById(id);

    return vendor;
  }

  @sanitiseParams()
  async updateVendor(vendorId: string, requestBody: PatchVendorRequestBody) {
    const { emails, portalOrderVendor } = requestBody;

    if (emails && new Set(emails).size !== emails.length) {
      throw new ValidationError("Duplicate emails", "emails");
    }

    if ((!emails || emails.length === 0) && portalOrderVendor === false) {
      throw new ValidationError("Emails cannot be empty", "emails");
    }

    if (emails && emails.length > 30) {
      throw new ValidationError("Too many emails", "emails");
    }

    const updatedVendor = await this.apiService.patchVendor(vendorId, requestBody);

    return updatedVendor;
  }

  @sanitiseParams()
  async deleteVendor(vendorId: string) {
    await this.apiService.deleteVendor(vendorId);
  }

  @sanitiseParams()
  async createVendor(requestBody: PostVendorRequestBody) {
    const { emails, portalOrderVendor } = requestBody;

    if (emails && new Set(emails).size !== emails.length) {
      throw new ValidationError("Duplicate emails", "emails");
    }

    if ((!emails || emails.length === 0) && portalOrderVendor === false) {
      throw new ValidationError("Emails cannot be empty", "emails");
    }

    if (emails && emails.length > 30) {
      throw new ValidationError("Too many emails", "emails");
    }

    const createdVendor = await this.apiService.postVendor(requestBody);

    return createdVendor;
  }

  @sanitiseParams()
  async vendorsExports(requestBody: PostVendorsExportsRequestBody): Promise<File> {
    const { signedUrl, fileName } = await this.apiService.postVendorsExports(requestBody);

    const fileBlob = await this.fileAPIService.getFile(signedUrl);

    return new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });
  }

  @sanitiseParams()
  async postVendorsBulkEmail(requestBody: PostVendorsBulkEmailRequestBody): Promise<SendBulkEmailResult> {
    return await this.apiService.postVendorsBulkEmail(requestBody);
  }
}

export { VendorService };
